import { useState } from "react";
import { MessageSquare, AlertTriangle, Download, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { api, useAuth } from "@/App";

function handleDownloadPhoto(photo, index) {
  try {
    const link = document.createElement('a');
    link.href = photo;
    link.download = `signalement_photo_${index + 1}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Téléchargement démarré");
  } catch (e) {
    window.open(photo, '_blank');
  }
}

export default function VehicleComments({ vehicleId, comments = [], onUpdate }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [deletingId, setDeletingId] = useState(null);

  const handleDeleteComment = async (commentId, index) => {
    if (!confirm("Supprimer ce commentaire ?")) return;
    
    setDeletingId(commentId || index);
    try {
      // Filter out the comment to delete
      const updatedComments = comments.filter((c, i) => 
        commentId ? c.id !== commentId : i !== index
      );
      
      await api.put(`/vehicles/${vehicleId}`, { comments: updatedComments });
      toast.success("Commentaire supprimé");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
    setDeletingId(null);
  };

  if (!comments || comments.length === 0) {
    return null;
  }

  return (
    <Card className="border-slate-200" data-testid="vehicle-comments">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Commentaires ({comments.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {comments.map((comment, idx) => (
          <div 
            key={comment.id || idx} 
            className={`p-3 rounded-lg border relative group ${
              comment.is_blocking && !comment.resolved 
                ? 'bg-red-50 border-red-300' 
                : comment.resolved 
                  ? 'bg-slate-100 border-slate-200 opacity-70'
                  : 'bg-slate-50 border-slate-200'
            }`}
            data-testid={`comment-${idx}`}
          >
            {/* Delete button for admin */}
            {isAdmin && (
              <button
                onClick={() => handleDeleteComment(comment.id, idx)}
                disabled={deletingId === (comment.id || idx)}
                className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                title="Supprimer le commentaire"
                data-testid={`delete-comment-${idx}`}
              >
                {deletingId === (comment.id || idx) ? (
                  <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Trash2 className="h-3 w-3" />
                )}
              </button>
            )}
            
            <div className="flex items-start justify-between pr-8">
              <div className="flex-1">
                {comment.is_blocking && !comment.resolved && (
                  <div className="flex items-center gap-1 text-red-600 text-xs font-bold mb-1">
                    <AlertTriangle className="h-3 w-3" />
                    DOSSIER BLOQUÉ
                  </div>
                )}
                {comment.resolved && (
                  <div className="flex items-center gap-1 text-emerald-600 text-xs font-medium mb-1">
                    ✓ Résolu par {comment.resolved_by}
                  </div>
                )}
                <p className="text-sm text-slate-700">{comment.message}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Par {comment.user_name} • {new Date(comment.created_at).toLocaleDateString('fr-FR', { 
                    day: '2-digit', 
                    month: '2-digit', 
                    year: '2-digit', 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </p>
              </div>
            </div>
            {comment.photo && (
              <div className="mt-2 relative group/photo inline-block">
                <img 
                  src={comment.photo} 
                  alt="Photo du signalement" 
                  className="max-h-40 rounded-lg border border-slate-200 cursor-pointer hover:opacity-80"
                  onClick={() => window.open(comment.photo, '_blank')}
                  data-testid={`comment-photo-${idx}`}
                />
                <button
                  onClick={(e) => { e.stopPropagation(); handleDownloadPhoto(comment.photo, idx); }}
                  className="absolute bottom-2 right-2 bg-blue-500 text-white rounded-full p-1.5 opacity-0 group-hover/photo:opacity-100 transition-opacity hover:bg-blue-600"
                  title="Télécharger"
                >
                  <Download className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
